import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import ImageUpload, { ImageUploadGrid } from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CATEGORIES, categoryLabel } from "@/lib/categories";

export default function AdminProductForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    description: "",
    price: "",
    category: "cuisine",
    image_url: "",
    image_public_id: "",
    additional_images: "",
    additional_images_list: [],
    supplier_id: "",
    supplier_name: "",
    supplier_contact: "",
    is_special: false,
    is_unique: false,
    stock: 100,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    api.get("/admin/suppliers")
      .then((r) => setSuppliers(r.data))
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (isEdit) {
      api.get(`/admin/products`).then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) {
          setForm({
            name: p.name || "",
            description: p.description || "",
            price: String(p.price || ""),
            category: p.category || "cuisine",
            image_url: p.image_url || "",
            image_public_id: p.image_public_id || "",
            additional_images: "",
            additional_images_list: Array.isArray(p.additional_images_list)
              ? p.additional_images_list
              : (p.additional_images || []).map((url) => ({ url, public_id: null })),
            supplier_id: p.supplier_id || "",
            supplier_name: p.supplier_name || "",
            supplier_contact: p.supplier_contact || "",
            is_special: p.is_special || false,
            is_unique: p.is_unique || false,
            stock: p.stock ?? 100,
          });
        }
      });
    }
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const price = parseFloat(form.price);
    if (!form.name || !price || price <= 0 || !form.category) {
      setError("Veuillez remplir les champs requis.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price,
        category: form.category,
        image_url: form.image_url,
        image_public_id: form.image_public_id || null,
        additional_images: form.additional_images_list.map((a) => a.url).filter(Boolean),
        additional_images_list: form.additional_images_list,
        supplier_id: form.supplier_id || null,
        supplier_name: form.supplier_name,
        supplier_contact: form.supplier_contact,
        is_special: form.is_special,
        is_unique: form.is_unique,
        stock: parseInt(form.stock, 10) || 0,
      };
      if (isEdit) {
        await api.patch(`/admin/products/${id}`, payload);
      } else {
        await api.post("/admin/products", payload);
      }
      navigate("/admin/catalog");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF9FB]">
      <TopBar />
      <div className="max-w-2xl mx-auto px-5 pb-24">
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/catalog")}
          data-testid="product-form-back"
          className="gap-2 -ml-3 mt-2 text-[#5C4664] hover:bg-[#FCE7F3]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("admin.catalog")}
        </Button>

        <h1 className="mt-6 font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
          {isEdit ? t("admin.catalogPage.editProduct") : t("admin.catalogPage.newProduct")}
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <Field label={t("admin.catalogPage.productForm.name")}>
            <Input
              data-testid="product-name-input"
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="h-11 rounded-xl border-[#F2E2EC]"
            />
          </Field>
          <Field label={t("admin.catalogPage.productForm.description")}>
            <Textarea
              data-testid="product-description-input"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className="rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t("admin.catalogPage.productForm.price")}>
              <Input
                data-testid="product-price-input"
                type="number"
                min="1"
                step="0.01"
                value={form.price}
                onChange={(e) => setField("price", e.target.value)}
                className="h-11 rounded-xl border-[#F2E2EC]"
              />
            </Field>
            <Field label={t("admin.catalogPage.productForm.category")}>
              <Select value={form.category} onValueChange={(v) => setField("category", v)}>
                <SelectTrigger data-testid="product-category-select" className="h-11 rounded-xl border-[#F2E2EC]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.slug} value={c.slug}>{categoryLabel(c.slug, lang)}</SelectItem>
                  ))}
                  <SelectItem value="special">{categoryLabel("special", lang)}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <ImageUpload
            value={form.image_url ? (form.image_public_id ? { url: form.image_url, public_id: form.image_public_id } : form.image_url) : null}
            onChange={(asset) => {
              setField("image_url", asset?.url || "");
              setField("image_public_id", asset?.public_id || "");
            }}
            folder="milkado/products/"
            label={t("admin.catalogPage.productForm.imageUrl")}
            testid="product-image"
          />

          <ImageUploadGrid
            value={form.additional_images_list || []}
            onChange={(list) => setField("additional_images_list", list)}
            folder="milkado/products/"
            testid="product-additional"
          />

          <div className="space-y-4">
            <Field label={t("admin.catalogPage.productForm.supplierName")}>
              <select
                data-testid="product-supplier-select"
                value={form.supplier_id}
                onChange={(e) => {
                  const id = e.target.value;
                  const found = suppliers.find((s) => s.id === id);
                  setForm((f) => ({
                    ...f,
                    supplier_id: id,
                    // Snapshot the name onto the legacy field so it works even
                    // if the supplier is later renamed/archived.
                    supplier_name: found ? found.name : f.supplier_name,
                    // Also auto-fill contact if available and the form is empty.
                    supplier_contact: found && !f.supplier_contact
                      ? (found.phone || found.email || "")
                      : f.supplier_contact,
                  }));
                }}
                className="w-full h-11 rounded-xl border border-[#F2E2EC] bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#D81B8C]"
              >
                <option value="">
                  {suppliers.length === 0
                    ? "— Aucun fournisseur dans le référentiel —"
                    : "— Choisir un fournisseur du référentiel —"}
                </option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {!form.supplier_id && form.supplier_name && (
                <p className="text-[11px] text-amber-700 mt-1">
                  Saisie libre conservée : <strong>{form.supplier_name}</strong>.
                  Sélectionnez un fournisseur du référentiel pour activer les rapports consolidés.
                </p>
              )}
              {suppliers.length === 0 && (
                <p className="text-[11px] text-[#5C4664] mt-1">
                  Demandez à un admin de créer des fournisseurs depuis l&apos;onglet
                  Finances → Fournisseurs.
                </p>
              )}
            </Field>
            <Field label={t("admin.catalogPage.productForm.supplierContact")}>
              <Input
                data-testid="product-supplier-contact-input"
                value={form.supplier_contact}
                onChange={(e) => setField("supplier_contact", e.target.value)}
                placeholder="Téléphone ou email du contact"
                className="h-11 rounded-xl border-[#F2E2EC]"
              />
            </Field>
          </div>

          <Field label={t("admin.catalogPage.productForm.stock")}>
            <Input
              data-testid="product-stock-input"
              type="number"
              min="0"
              value={form.stock}
              onChange={(e) => setField("stock", e.target.value)}
              className="h-11 rounded-xl border-[#F2E2EC]"
            />
          </Field>

          <label className="flex items-center justify-between p-4 rounded-xl bg-white border border-[#F2E2EC]">
            <span className="text-sm text-[#2A0E3C] pr-3">
              {t("admin.catalogPage.productForm.isSpecial")}
            </span>
            <Switch
              data-testid="product-special-switch"
              checked={form.is_special}
              onCheckedChange={(v) => setField("is_special", v)}
              className="data-[state=checked]:bg-[#6B2C8F]"
            />
          </label>

          <label className="flex items-center justify-between p-4 rounded-xl bg-white border border-[#F2E2EC]">
            <span className="text-sm text-[#2A0E3C] pr-3">
              {t("admin.catalogPage.productForm.isUnique")}
            </span>
            <Switch
              data-testid="product-unique-switch"
              checked={form.is_unique}
              onCheckedChange={(v) => setField("is_unique", v)}
              className="data-[state=checked]:bg-[#D81B8C]"
            />
          </label>

          {error && (
            <p data-testid="product-form-error" className="text-sm text-[#D81B8C] font-medium">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="product-submit"
            className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-14 font-semibold text-base"
          >
            {loading ? t("common.loading") : isEdit
              ? t("admin.catalogPage.productForm.update")
              : t("admin.catalogPage.productForm.create")}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-[#5C4664]">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
